import React, { forwardRef } from 'react'
import { Controller, UseControllerProps } from 'react-hook-form'
import { AsyncSelectBox } from '@saasalerts/ui'
import { SelectOption } from '@/types/reports'
import { SelectInstance, GroupBase, Options } from 'react-select'
import { AsyncProps } from 'react-select/async'
import { AsyncCreatableProps } from 'react-select/async-creatable'

export const AsyncSelect = forwardRef<
  SelectInstance<unknown, boolean, GroupBase<Options<unknown>>>,
  AsyncProps<unknown, boolean, GroupBase<Options<unknown>>> &
    AsyncCreatableProps<unknown, boolean, GroupBase<Options<unknown>>> & {
      fieldProps: { label: string; creatable?: boolean }
      controllerProps: UseControllerProps
      errorMessage?: string
      asyncProps: AsyncProps<unknown, boolean, GroupBase<Options<unknown>>>
      toolTipText?: string
    }
>(
  ({
    fieldProps: { label, creatable },
    controllerProps,
    asyncProps,
    errorMessage,
    isValidNewOption,
    toolTipText,
  }) => {
    return (
      <Controller
        render={({ field: { onChange, value, ...otherFields } }) => {
          /**
           * This method finds the value in the list of default options and returns the options.
           *
           * This is a coupled method which makes this component non-usable in async mode.
           * i.e. the options have to be preloaded for this component to work
           */
          const getValue = (() => {
            // If there is no value, return null, react-select does not change value when returning undefined.
            if (!value) {
              return null
            }

            const findOption = (v: string) =>
              (asyncProps.defaultOptions as SelectOption[])?.find(
                (o) => o.id === v
              ) ?? {
                id: v,
                name: v,
              } // added a default because a newly created value (creatable) wont exist in default options

            if (Array.isArray(value)) {
              return value.map(findOption)
            }

            // @ts-ignore - difficult to fix this type
            return findOption(value)
          })()

          /**
           * This method returns the ids of the options based on the selected items / item
           */
          const handleChange = (
            args: Record<string, any> | Record<string, any>[]
          ) => {
            if (Array.isArray(args)) {
              onChange(args.map((arg) => arg.id))
            } else {
              onChange(args.id)
            }
          }
          return (
            <AsyncSelectBox
              {...asyncProps}
              {...otherFields}
              label={label}
              creatable={creatable}
              isValidNewOption={isValidNewOption}
              name={controllerProps.name}
              value={getValue}
              errorMessage={errorMessage}
              menuPortalTarget={document.getElementById('react-select-portal')}
              onChange={handleChange as (value: unknown) => void}
              toolTipText={toolTipText}
            />
          )
        }}
        name={controllerProps.name}
        control={controllerProps.control}
        rules={controllerProps.rules}
      />
    )
  }
)
